import { useState } from 'react'
import { TableShell } from '../../components/molecules/TableShell'
import { Alert } from '../../components/atoms/Alert'
import { Badge } from '../../components/atoms/Badge'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { Paginator } from '../../components/molecules/Paginator'
import { GroupFormModal } from './GroupFormModal'
import { useGroups, type GroupDto } from '../../../viewmodels/useGroups'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /iam/groups — manage named permission templates (Layer 2c).
 *
 * Per .claude/ui/07-per-screen-flows-iam.md.
 */
export function IamGroupsPage(): JSX.Element {
  const {
    items,
    screenActions,
    total,
    page,
    pageSize,
    isLoading,
    error,
    setPage,
    reload,
    create,
    update,
    remove
  } = useGroups()
  const { can } = useAuth()
  const [modalMode, setModalMode] = useState<'closed' | 'create' | 'edit'>('closed')
  const [editTarget, setEditTarget] = useState<GroupDto | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const openCreate = (): void => {
    setEditTarget(null)
    setModalMode('create')
  }
  const openEdit = (g: GroupDto): void => {
    setEditTarget(g)
    setModalMode('edit')
  }
  const closeModal = (): void => setModalMode('closed')

  const handleDelete = async (g: GroupDto): Promise<void> => {
    const msg = `Delete group "${g.name}"? This cannot be undone.`
    if (typeof window !== 'undefined' && !window.confirm(msg)) return
    setDeletingId(g.id)
    setDeleteError(null)
    const res = await remove(g.id)
    setDeletingId(null)
    if (!res.ok) setDeleteError(res.message)
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <h1 className="font-heading text-h2 text-charcoal">Groups</h1>
        {can(SCREENCODES.IAMGROUPS, 'add') ? <Button onClick={openCreate}>Add</Button> : null}
      </header>

      {deleteError !== null ? <Alert tone="error">{deleteError}</Alert> : null}
      {error !== null ? (
        <Alert tone="error">
          {error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={() => void reload()}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <EmptyState onAdd={openCreate} />
      ) : (
        <>
          <Table items={items} onEdit={openEdit} onDelete={(g) => void handleDelete(g)} deletingId={deletingId} />
          <Paginator
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            disabled={isLoading}
          />
        </>
      )}

      <GroupFormModal
        open={modalMode !== 'closed'}
        mode={modalMode === 'edit' ? 'edit' : 'create'}
        initial={editTarget}
        screenActions={screenActions}
        onClose={closeModal}
        onSubmit={async (input) => {
          if (modalMode === 'edit' && editTarget !== null) {
            return update(editTarget.id, input)
          }
          return create(input as Parameters<typeof create>[0])
        }}
      />
    </div>
  )
}

function EmptyState({ onAdd }: { readonly onAdd: () => void }): JSX.Element {
  const { can } = useAuth()
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-heading text-h3 text-charcoal">No groups yet</p>
      <p className="font-body text-body-sm text-text-secondary mt-2">
        Create a group to bundle screen actions together.
      </p>
      {can(SCREENCODES.IAMGROUPS, 'add') ? (
        <div className="mt-4">
          <Button onClick={onAdd}>Add</Button>
        </div>
      ) : null}
    </div>
  )
}

function Table({
  items,
  onEdit,
  onDelete,
  deletingId
}: {
  readonly items: GroupDto[]
  readonly onEdit: (g: GroupDto) => void
  readonly onDelete: (g: GroupDto) => void
  readonly deletingId: number | null
}): JSX.Element {
  const { can } = useAuth()
  const canEdit = can(SCREENCODES.IAMGROUPS, 'edit')
  const canDelete = can(SCREENCODES.IAMGROUPS, 'delete')
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Name</Th>
            <Th>Description</Th>
            <Th>Active</Th>
            <Th>Screens</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((g) => (
            <tr key={g.id} className="border-t border-border">
              <Td>
                <span className="font-body text-body-sm text-charcoal">{g.name}</span>
              </Td>
              <Td>
                <span className="font-body text-body-xs text-text-secondary">
                  {g.description ?? '—'}
                </span>
              </Td>
              <Td>
                {g.is_active === 1 ? (
                  <Badge tone="success">Active</Badge>
                ) : (
                  <Badge tone="warning">Inactive</Badge>
                )}
              </Td>
              <Td>
                <span className="font-body text-body-sm text-charcoal">
                  {g.grants.length}
                </span>
              </Td>
              <Td className="text-right whitespace-nowrap">
                {canEdit ? (
                  <Button size="sm" variant="secondary" onClick={() => onEdit(g)}>
                    Edit
                  </Button>
                ) : null}{canEdit && canDelete ? ' ' : null}
                {canDelete ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(g)}
                    disabled={deletingId === g.id}
                  >
                    {deletingId === g.id ? 'Deleting…' : 'Delete'}
                  </Button>
                ) : null}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableShell>
  )
}
